import React, { useEffect, useMemo, useState } from "react";
import { Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { DeleteAccountDialog } from "@/components/DeleteAccountDialog";

const ROLE = { pool_owner: "Poolbesitzer / Großverbraucher", municipality: "Gemeinde", utility: "Wasserleitungsverband" };

export const UsersPanel = ({ onChanged }) => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [target, setTarget] = useState(null);

  const load = () => api.get("/admin/users").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? items.filter((u) => `${u.name} ${u.email} ${u.municipality_name}`.toLowerCase().includes(s)) : items;
  }, [items, q]);

  const onConfirm = async (body) => {
    await api.post(`/admin/users/${target.id}/delete`, body);
    toast.success(`Konto ${target.email} gelöscht`);
    setTarget(null);
    load();
    onChanged?.();
  };

  return (
    <Card data-testid="admin-users-panel">
      <CardContent className="space-y-4 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 text-lg font-semibold"><Users className="h-5 w-5" /> Nutzerkonten ({items.length})</h2>
          <Input className="max-w-xs" placeholder="Suchen…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-users-search" />
        </div>
        <div className="max-h-[560px] overflow-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted text-left">
              <tr><th className="p-2">Name</th><th className="p-2">E-Mail</th><th className="hidden p-2 md:table-cell">Rolle</th><th className="hidden p-2 md:table-cell">Gemeinde</th><th className="p-2" /></tr>
            </thead>
            <tbody>
              {shown.map((u) => (
                <tr key={u.id} className="border-t border-border" data-testid={`admin-user-row-${u.id}`}>
                  <td className="p-2 font-medium">{u.name || "–"}</td>
                  <td className="break-all p-2">{u.email}</td>
                  <td className="hidden p-2 md:table-cell">{ROLE[u.role] || "ohne Rolle"}{u.account_status !== "active" && <Badge variant="outline" className="ml-2">{u.account_status}</Badge>}</td>
                  <td className="hidden p-2 md:table-cell">{u.municipality_name || "–"}</td>
                  <td className="p-2 text-right">
                    {u.is_super_admin ? <Badge>Super-Admin</Badge> : (
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setTarget(u)} data-testid={`admin-user-delete-${u.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {!shown.length && (
                <tr><td colSpan={5} className="p-6 text-center text-muted-foreground" data-testid="admin-users-empty">Keine Nutzer gefunden</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
      <DeleteAccountDialog
        open={!!target}
        onOpenChange={(v) => !v && setTarget(null)}
        title={`Konto löschen: ${target?.name || target?.email || ""}`}
        description={`${target?.email || ""} (${ROLE[target?.role] || "ohne Rolle"}) wird unwiderruflich gelöscht. Personenbezogene Daten werden entfernt, Buchungsmengen bleiben anonymisiert erhalten. Bei Poolbesitzern und Großverbrauchern wird die Gemeinde informiert.`}
        passwordLabel="Ihr Super-Admin-Passwort"
        onConfirm={onConfirm}
      />
    </Card>
  );
};
